import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone } from 'lucide-react';
import { contactInfo } from '../data/mock';

const PrivacyPolicy = () => {
  const lastUpdated = "February 1, 2026";

  const sections = [
    {
      title: "1. Introduction",
      content: (
        <>
          <p>
            OmniCore X Quantum (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, share, and safeguard information when you visit our website (<span className="text-cyan-400">omnicorexquantum.com</span>), interact with our marketing, or use any of our services.
          </p>
          <p className="mt-4">
            By accessing or using our website and services, you consent to the practices described in this Privacy Policy. If you do not agree, please discontinue use of our website.
          </p>
        </>
      )
    },
    {
      title: "2. Information We Collect",
      content: (
        <>
          <p>We collect different categories of information to provide and improve our services:</p>
          <h4 className="text-white font-semibold mt-6 mb-2">a) Information You Provide Directly</h4>
          <ul className="list-disc pl-6 space-y-2 text-gray-400">
            <li>Full name, email address, phone number, and WhatsApp number</li>
            <li>Business name, industry, role and project requirements</li>
            <li>Messages or inquiries submitted via contact forms, consultation requests, or chat</li>
            <li>Newsletter subscription email addresses</li>
            <li>Any other information you voluntarily share with us</li>
          </ul>
          <h4 className="text-white font-semibold mt-6 mb-2">b) Information Collected Automatically</h4>
          <ul className="list-disc pl-6 space-y-2 text-gray-400">
            <li>IP address, browser type, operating system, and device identifiers</li>
            <li>Pages visited, time spent, click activity, referring URLs</li>
            <li>Cookies, pixels, and similar tracking technologies</li>
            <li>Google Analytics 4 (GA4) usage and performance data</li>
          </ul>
          <h4 className="text-white font-semibold mt-6 mb-2">c) Information from Third Parties</h4>
          <p>
            We may receive limited information from advertising platforms, analytics providers, and integrated services such as Meta Ads, Google Ads, LinkedIn, and CRM tools, in accordance with their respective policies.
          </p>
        </>
      )
    },
    {
      title: "3. How We Use Your Information",
      content: (
        <>
          <p>We use the information we collect for the following purposes:</p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>To respond to inquiries, schedule consultations, and deliver requested services</li>
            <li>To send proposals, quotations, contracts, and project updates</li>
            <li>To send newsletters, marketing communications, and promotional offers (with opt-out option)</li>
            <li>To improve our website, services, content, and user experience</li>
            <li>To analyze trends, track campaign performance, and conduct internal research</li>
            <li>To detect, prevent, and address technical issues, fraud, or unauthorized activity</li>
            <li>To comply with legal obligations, court orders, or government requests</li>
          </ul>
        </>
      )
    },
    {
      title: "4. Cookies and Tracking Technologies",
      content: (
        <>
          <p>
            Our website uses cookies and similar technologies to enhance user experience and gather analytics. Cookies are small text files stored on your device that help us recognize you and remember your preferences.
          </p>
          <p className="mt-4">We use the following types of cookies:</p>
          <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-400">
            <li><strong className="text-white">Essential cookies:</strong> Required for the website to function correctly</li>
            <li><strong className="text-white">Analytics cookies:</strong> Google Analytics 4 to measure usage and performance</li>
            <li><strong className="text-white">Marketing cookies:</strong> To deliver relevant ads and measure campaign effectiveness</li>
          </ul>
          <p className="mt-4">
            You may disable cookies through your browser settings; however, certain features of the website may not function properly.
          </p>
        </>
      )
    },
    {
      title: "5. How We Share Your Information",
      content: (
        <>
          <p>
            We do not sell, rent, or trade your personal information. We may share data only in the following circumstances:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li><strong className="text-white">Service Providers:</strong> Trusted third-party vendors who help us operate our business (hosting, email delivery, analytics, CRM, payment processors)</li>
            <li><strong className="text-white">Legal Compliance:</strong> When required by law, regulation, legal process, or government request</li>
            <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with prior notice to you</li>
            <li><strong className="text-white">With Your Consent:</strong> Any other sharing will be done only with your explicit permission</li>
          </ul>
        </>
      )
    },
    {
      title: "6. Data Retention",
      content: (
        <p>
          We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with our legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, we securely delete or anonymize it.
        </p>
      )
    },
    {
      title: "7. Data Security",
      content: (
        <>
          <p>
            We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These include:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li>SSL/TLS encryption for data transmission</li>
            <li>Secure password hashing (bcrypt) for credentials</li>
            <li>JWT-based authentication for admin systems</li>
            <li>Restricted access to personal data on a need-to-know basis</li>
            <li>Regular security audits and monitoring</li>
          </ul>
          <p className="mt-4">
            However, no method of internet transmission or electronic storage is 100% secure. We cannot guarantee absolute security.
          </p>
        </>
      )
    },
    {
      title: "8. Your Privacy Rights",
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the following rights:</p>
          <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-400">
            <li><strong className="text-white">Right to Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong className="text-white">Right to Rectification:</strong> Correct inaccurate or incomplete information</li>
            <li><strong className="text-white">Right to Erasure:</strong> Request deletion of your personal data (&ldquo;right to be forgotten&rdquo;)</li>
            <li><strong className="text-white">Right to Object:</strong> Object to processing for marketing or other purposes</li>
            <li><strong className="text-white">Right to Data Portability:</strong> Receive your data in a structured, commonly used format</li>
            <li><strong className="text-white">Right to Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
          </ul>
          <p className="mt-4">
            To exercise these rights, contact us at <span className="text-cyan-400">{contactInfo.email}</span>. We will respond within 30 days.
          </p>
        </>
      )
    },
    {
      title: "9. Children's Privacy",
      content: (
        <p>
          Our website and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal data, we will delete such information promptly.
        </p>
      )
    },
    {
      title: "10. Third-Party Links",
      content: (
        <p>
          Our website may contain links to third-party websites, plugins, or services. We are not responsible for the privacy practices or content of those third parties. We encourage you to read their privacy policies before providing any personal information.
        </p>
      )
    },
    {
      title: "11. International Data Transfers",
      content: (
        <p>
          Your information may be processed and stored in countries other than your country of residence. We take appropriate safeguards to ensure that your personal data is treated securely and in accordance with this Privacy Policy and applicable data protection laws.
        </p>
      )
    },
    {
      title: "12. Marketing Communications",
      content: (
        <p>
          If you have subscribed to our newsletter or consented to receive marketing communications, you may opt out at any time by clicking the &ldquo;unsubscribe&rdquo; link at the bottom of any marketing email or by contacting us directly at <span className="text-cyan-400">{contactInfo.email}</span>.
        </p>
      )
    },
    {
      title: "13. Changes to This Privacy Policy",
      content: (
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or for other operational reasons. The updated version will be indicated by a revised &ldquo;Last Updated&rdquo; date and will be effective immediately upon posting. We encourage you to review this policy periodically.
        </p>
      )
    },
    {
      title: "14. Contact Us",
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3 text-gray-400">
              <Mail className="w-5 h-5 text-cyan-400" />
              <a href={`mailto:${contactInfo.email}`} className="hover:text-cyan-400 transition-colors">
                {contactInfo.email}
              </a>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <Phone className="w-5 h-5 text-cyan-400" />
              <a href={`tel:+91${contactInfo.phone}`} className="hover:text-cyan-400 transition-colors">
                +91 {contactInfo.phone}
              </a>
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 mb-6">
            <Shield className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Privacy <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Policy</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Your privacy matters. Learn how we collect, use, and protect your information.
          </p>
          <p className="text-sm text-gray-500 mt-4">Last Updated: {lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div
              key={section.title}
              className="bg-gradient-to-br from-gray-900/60 to-black/60 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/40 transition-all duration-300"
              data-testid={`privacy-section-${index + 1}`}
            >
              <h2 className="text-2xl font-bold text-white mb-4">{section.title}</h2>
              <div className="text-gray-400 leading-relaxed">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">
            Have questions about how we handle your data?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300"
            data-testid="privacy-contact-cta"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
